import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Modal,
    Box,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid
} from "@material-ui/core";
import { KeyboardTimePicker } from "@material-ui/pickers";
import EditIcon from '@material-ui/icons/Edit';
import {
    USER_OPERATIONS,
    RESOURCE_TYPE,
    API_RESOURCE_URLS,
    MESSAGE_TYPE,
    DATE_FORMAT,
    ASN_STATUS,
    ZPL_ASN_TEMPLATE_FOR_DROPPING,
} from "../../constants";
import defaultStyles from "./PBSDroppingUpdate.module.css";
import "../../cssModules/switch.css";
import { BackButton } from "..";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { useHistory } from "react-router-dom";
import { API } from "../../apis/api";
import { statusStringMap } from '../../staticData';
import { downloadCsv } from "../../utils/utils";
import { buildErrorMessage } from "../../apis/calls";
import moment from "moment";
import PbsDroppingToolbar from "../PbsDroppingToolbar/PbsDroppingToolbar";

const PBSDroppingUpdate = ({
    setHasActiveEdits,
    hasActiveEdits,
    handleCountChange,
}) => {
    const ROW_HEIGHT = 38;
    const RESOURCE_OPERATION_UPDATE = USER_OPERATIONS.UPDATE_CHASSIS_PLAN_ASN;
    const { showError, showInternalError, showPopup, showLoading, closePopUp } = usePopupManager();
    const [plant] = usePlantAndLinesStore().plantState;

    const history = useHistory();

    const [styles, setStyles] = useState(defaultStyles);

    const [rowsData, setRowsData] = useState({
        loading: false,
        data: [],
        filteredData: [],
    });



    const [asnCount, setAsnCount] = useState(0);
    const [selectedDate, setSelectedDate] = useState(moment());
    const [openPbsDialog, setOpenPbsDialog] = useState(false);
    const [selectedPbsRow, setSelectedPbsRow] = useState(null);
    const [biwSerialNumber, setBiwSerialNumber] = useState("");
    const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
    const [summaryStartTime, setSummaryStartTime] = useState(null);
    const [summaryEndTime, setSummaryEndTime] = useState(null);
    const [summaryResults, setSummaryResults] = useState([]);
    const [summaryTotals, setSummaryTotals] = useState({ totalQty: 0, totalPending: 0 });
    const [summaryTimeRange, setSummaryTimeRange] = useState("");

    const setCount = (count) => {
        setAsnCount(count);
    };

    const loadPlanDataOfSelectedDate = () => {
        const startDateString = selectedDate.clone().startOf('day').format(DATE_FORMAT.ISO);
        const endDateString = selectedDate.clone().endOf('day').format(DATE_FORMAT.ISO);
        return getPbsDroppingData(startDateString, endDateString);
    };

    const fixedColumns = [
        {
            width: 150,
            title: 'ASN',
            field: 'asn',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 120,
            title: 'VC',
            field: 'vc',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 230,
            title: 'Description',
            field: 'vcDescription',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 120,
            title: 'Shift',
            field: 'shiftName',
            enableSearch: true,
            enableFilter: true,
            render: ({ shiftName }) => (
                <div style={{ textAlign: 'center' }}>
                    {shiftName && (
                        <div
                            className={styles.shift}
                        >
                            {shiftName}
                        </div>
                    )}
                </div>
            ),
        },
        {
            width: 200,
            title: 'Planned FDT',
            field: 'plannedDropTimestamp',
            render: row =>
                row.plannedDropTimestamp
                    ? moment(row.plannedDropTimestamp).format('hh:mm:ss A DD/MM/YYYY')
                    : null,
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 140,
            title: 'VC Status',
            field: 'vcStatus',
            render: row => {
                if (row.status === ASN_STATUS.PLANNED)
                    return <span style={{ opacity: 0.5 }}>Pending</span>;
                if (row.edited) return 'Pending';
                return statusStringMap[row.status];
            },
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 120,
            title: 'Line',
            field: 'line',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 160,
            title: 'Pbs Out Status',
            field: 'pbsOutStatus',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 200,
            title: 'BIW Serial Number',
            field: 'biwSerialNumber',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 200,
            title: 'BIW Part Number',
            field: 'biwPartNumber',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 140,
            title: 'Colour',
            field: 'colour',
            enableSearch: true,
            enableFilter: true,
        },
    ].filter(c => c);

    const dropTableColumns = [
        {
            width: 240,
            title: 'Pbs Out Timestamp',
            field: 'pbsOutTimestamp',
            render: row =>
                row.pbsOutTimestamp
                    ? moment(row.pbsOutTimestamp).format('hh:mm:ss A DD/MM/YYYY')
                    : null,
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 200,
            title: 'PowerSteering',
            field: 'powerSteering',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 120,
            title: 'BC No.',
            field: 'buildCardNumber',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 120,
            title: 'Defence',
            field: 'defence',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 120,
            title: 'Market',
            field: 'market',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 140,
            title: 'Category',
            field: 'category',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 140,
            title: 'Country',
            field: 'country',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 110,
            title: 'FBV',
            field: 'fbv',
            enableSearch: true,
            enableFilter: true,
        },
        {
            width: 140,
            title: 'Major Model',
            field: 'majorModel',
            enableSearch: true,
            enableFilter: true,
        },
    ];

    const redirectToApplicationLandingPage = () => {
        history.push("/");
    };

    const getPbsDroppingData = async (fromDate, toDate) => {
        if (plant.value) {
            setRowsData({ loading: true, ...rowsData });

            try {
                showLoading();
                const response = await API.get(API_RESOURCE_URLS.getPbsDroppingData(plant.value), {
                    params: {
                        from: fromDate,
                        to: toDate,
                        status: [ASN_STATUS.PLANNED]
                    },
                });

                const lines = response.data?.lines || [];

                // Sort lines by lineName
                // lines.sort((a, b) => (a.lineName || '').localeCompare(b.lineName || ''));

                let allPlans = [];

                lines.forEach(line => {
                    const linePlans = (line.vcAsnPlans || []).map(plan => ({
                        ...plan,
                        line: line.lineName
                    }));
                    allPlans = [...allPlans, ...linePlans];
                });

                const validPlans = allPlans.filter(p => p.plannedDropTimestamp);
                const nullPlans = allPlans.filter(p => !p.plannedDropTimestamp);

                // Sort valid plans by plannedDropTimestamp
                validPlans.sort((a, b) => new Date(a.plannedDropTimestamp) - new Date(b.plannedDropTimestamp));

                // Sort null plans by ASN
                nullPlans.sort((a, b) => (a.asn || '').localeCompare(b.asn || ''));

                // Merge plans
                let plans = [...validPlans, ...nullPlans];

                // Fill null timestamps
                let lastTimestamp = null;
                plans.forEach(plan => {
                    if (plan.plannedDropTimestamp) {
                        lastTimestamp = moment(plan.plannedDropTimestamp);
                    } else if (lastTimestamp) {
                        lastTimestamp.add(20, 'seconds');
                        plan.plannedDropTimestamp = lastTimestamp.toISOString();
                    }
                });

                // Fetch updated PBS dropping data
                const pbsOutResponse = await API.get(API_RESOURCE_URLS.getUpdatedPbsDroppingData(plant.value));
                const pbsOutData = pbsOutResponse.data || [];
                const pbsOutMap = {};
                pbsOutData.forEach(item => {
                    if (item.asnId) {
                        pbsOutMap[item.asnId] = item;
                    }
                });

                // Bind updated data to plans
                plans = plans.map(plan => {
                    const pbsInfo = pbsOutMap[plan.asn];
                    return {
                        ...plan,
                        pbsOutStatus: pbsInfo ? pbsInfo.statusUpdate : null,
                        pbsOutTimestamp: pbsInfo ? pbsInfo.updateTs : null,
                        biwSerialNumber: pbsInfo ? pbsInfo.serialNumber : null,
                        biwPartNumber: pbsInfo ? pbsInfo.partNumber : null,
                    };
                });

                const updatedData = plans;

                setRowsData({
                    loading: false,
                    data: updatedData,
                    filteredData: updatedData,
                });
                closePopUp();
            } catch (err) {
                console.log(err);
                showError({
                    contextText: 'Failed to get the PBS Dropping data',
                    error: err,
                });
            }
        }
    };

    const handleUpdatePbsDroppingStatus = useCallback((selectedRows) => {
        if (selectedRows && selectedRows.length === 1) {
            const row = selectedRows[0];
            if (!row.pbsOutStatus) {
                setSelectedPbsRow(row);
                setBiwSerialNumber("");
                setOpenPbsDialog(true);
            }
        }
    }, []);

    const handleRowSelection = useCallback((selectedRows) => {
        if (selectedRows.length === 1 && selectedRows[0].status === ASN_STATUS.PLANNED) {
            handleUpdatePbsDroppingStatus(selectedRows);
        }
    }, [handleUpdatePbsDroppingStatus]);

    const handlePbsDialogClose = () => {
        setOpenPbsDialog(false);
        setSelectedPbsRow(null);
        setBiwSerialNumber("");
        setRowsData(prev => ({
            ...prev,
            data: [...prev.data],
            filteredData: [...prev.filteredData]
        }));
    };

    const executeReprint = (rows) => {
        const labels = rows.map(row => {
            const { asn, vc, vcDescription, line, biwPartNumber, biwSerialNumber } = row;

            return ZPL_ASN_TEMPLATE_FOR_DROPPING
                .replaceAll("{LineID}", line || '')
                .replaceAll("{ASN}", asn || '')
                .replaceAll("{MaterialNumber}", vc || '')
                .replaceAll("{MaterialDescription}", vcDescription || '')
                .replaceAll("{EngineMaterialDescription}", biwPartNumber || '')
                .replaceAll("{EngineMaterialNumber}", biwSerialNumber || '');
        });
        const zplstring = encodeURIComponent(labels.join("\n"));

        window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
    };

    const handlePbsSubmit = async () => {
        if (!biwSerialNumber || !biwSerialNumber.trim()) {
            showError({
                contextText: 'Validation Error',
                error: { message: 'Please enter BIW Serial Number' }
            });
            return;
        }

        if (plant.value && selectedPbsRow) {
            try {
                const payload = {
                    asnId: selectedPbsRow.asnId,
                    status: "PBS OUT",
                    vc: selectedPbsRow.vc,
                    asn: selectedPbsRow.asn,
                    serialNumber: biwSerialNumber.trim(),
                };
                showLoading();
                await API.put(API_RESOURCE_URLS.updatePbsStatus(plant.value), payload);
                showPopup({
                    type: MESSAGE_TYPE.SUCCESS,
                    contextText: ' ',
                    info: 'BIW Serial Number Validated Successfully',
                });
                executeReprint([{
                    ...selectedPbsRow,
                    biwSerialNumber: biwSerialNumber.trim(),
                    biwPartNumber: selectedPbsRow.biwPartNumber
                }]);
                handlePbsDialogClose();
                loadPlanDataOfSelectedDate();
            } catch (error) {
                handlePbsDialogClose();
                showError({
                    contextText: 'Failed to Validate BIW Serial Number',
                    error: error,
                });
            }
        }
    };

    const handleSummaryClick = () => {
        setOpenSummaryDialog(true);
        // Default start/end time to current time or sensible defaults if needed
        setSummaryStartTime(moment());
        setSummaryEndTime(moment());
    };

    const handleSummaryClose = () => {
        setOpenSummaryDialog(false);
        setSummaryStartTime(null);
        setSummaryEndTime(null);
        setSummaryResults([]);
        setSummaryTimeRange("");
    };

    const handleSummaryReset = () => {
        setSummaryStartTime(moment());
        setSummaryEndTime(moment());
        setSummaryResults([]);
        setSummaryTimeRange("");
    };

    const handleSummarySubmit = () => {
        if (!summaryStartTime || !summaryEndTime) return;

        const start = moment(selectedDate).set({
            hour: summaryStartTime.hour(),
            minute: summaryStartTime.minute(),
            second: 0
        });
        const end = moment(selectedDate).set({
            hour: summaryEndTime.hour(),
            minute: summaryEndTime.minute(),
            second: 59
        });

        setSummaryTimeRange(`${start.format('LT')} - ${end.format('LT')}`);

        // Filter data based on plannedDropTimestamp within the selected time range
        const filtered = rowsData.data.filter(row => {
            if (!row.plannedDropTimestamp) return false;
            const dropTime = moment(row.plannedDropTimestamp);
            return dropTime.isSameOrAfter(start) && dropTime.isSameOrBefore(end);
        });

        // Aggegate data
        const grouped = {};
        let totalQty = 0;
        let totalPending = 0;

        filtered.forEach(row => {
            const vc = row.vc;
            const line = row.line || 'Unknown';
            // Pending logic: Item is pending if status is NOT 'PBS OUT' or is null/undefined
            const status = row.pbsOutStatus ? String(row.pbsOutStatus).trim().toUpperCase() : '';
            const isPending = status !== 'PBS OUT';

            if (!grouped[vc]) {
                grouped[vc] = {
                    vc,
                    totalVcQty: 0,
                    lines: {}
                };
            }
            grouped[vc].totalVcQty++;

            if (!grouped[vc].lines[line]) {
                grouped[vc].lines[line] = {
                    line,
                    lineQty: 0,
                    linePending: 0
                };
            }
            grouped[vc].lines[line].lineQty++;

            if (isPending) {
                grouped[vc].lines[line].linePending++;
                totalPending++;
            }
            totalQty++;
        });

        // Convert grouped object to array
        const results = Object.values(grouped).map(group => {
            return {
                ...group,
                lines: Object.values(group.lines).sort((a, b) => a.line.localeCompare(b.line))
            };
        });

        // Sort results by VC
        results.sort((a, b) => a.vc.localeCompare(b.vc));

        setSummaryResults(results);
        setSummaryTotals({ totalQty, totalPending });
    };

    const prepareJsonHeaderAndValueFormats = () => {
        return [
            { label: "ASN", value: "asn" },
            { label: "VC", value: "vc" },
            { label: "Description", value: "vcDescription" },
            { label: "Shift", value: "shiftName" },
            { label: "Planned FDT", value: (row) => row.plannedDropTimestamp ? moment(row.plannedDropTimestamp).format('hh:mm A DD/MM/YYYY') : '' },
            { label: "VC Status", value: "vcStatus" },
            { label: "Line", value: "line" },
            { label: "Pbs Out Status", value: "pbsOutStatus" },
            { label: "Pbs Out Timestamp", value: (row) => row.pbsOutTimestamp ? moment(row.pbsOutTimestamp).format('hh:mm A DD/MM/YYYY') : '' },
            { label: "PowerSteering", value: "powerSteering" },
            { label: "BC No.", value: "buildCardNumber" },
            { label: "Defence", value: "defence" },
            { label: "Market", value: "market" },
            { label: "Category", value: "category" },
            { label: "Country", value: "country" },
            { label: "FBV", value: "fbv" },
            { label: "Colour", value: "colour" },
            { label: "Major Model", value: "majorModel" },
        ];
    };

    const generateAndDownloadPbsDroppingCsvReport = (fetchedData) => {
        try {
            if (!rowsData.loading) {
                const dateValue = moment().format(DATE_FORMAT.ISO);

                const jsonData = fetchedData;

                const allPbsDropping = [];

                const { parseAsync } = require("json2csv");
                const fields = prepareJsonHeaderAndValueFormats(allPbsDropping);
                const opts = { fields };
                parseAsync(jsonData, opts)
                    .then((csv) => {
                        const exportedFilename =
                            "PBS_Dropping_Update_" +
                            plant.label +
                            "_" +
                            dateValue +
                            ".csv";
                        downloadCsv(csv, exportedFilename);
                    })
                    .catch((error) =>
                        console.error(
                            "Failed to download PBS_Dropping_Update report, error:" +
                            buildErrorMessage(error)
                        )
                    );
            }
        } catch (error) {
            console.error(
                "Failed to download PBS_Dropping_Update report, error:" +
                buildErrorMessage(error)
            );
        }
    };

    useEffect(() => {
        handleCountChange(asnCount);
    }, [asnCount, handleCountChange]);

    useEffect(() => {
        setHasActiveEdits(rowsData.data.some((row) => row.edited));
    }, [rowsData.data, setHasActiveEdits]);

    useEffect(() => {
        loadPlanDataOfSelectedDate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plant.value, selectedDate]);

    return (
        <>
            <div className={styles.backButton}>
                <BackButton
                    action="PBS Dropping Update"
                    handleClick={redirectToApplicationLandingPage}
                />
            </div>
            <PbsDroppingToolbar
                plantName={plant.label}
                selectedDate={selectedDate}
                handleDateSelect={setSelectedDate}
                useCalendar={true}
                onSummaryClick={handleSummaryClick}
            />
            <div className={styles.container}>
                <div className={styles.tableView} style={{ height: "100%" }}>
                    <Table
                        columns={[...fixedColumns, ...dropTableColumns]}
                        defaultColumns={[...fixedColumns]}
                        rowHeight={ROW_HEIGHT}
                        rows={rowsData.filteredData}
                        setHasActiveEdits={setHasActiveEdits}
                        setAsnCount={setCount}
                        isDataLoading={rowsData.loading}
                        primaryAction={{
                            name: "Update PBS Status",
                            actionFn: (selectedRows) => handleUpdatePbsDroppingStatus(selectedRows),
                            authOperation: RESOURCE_OPERATION_UPDATE,
                            shouldEnable: (selectedRows) => selectedRows.length === 1 && selectedRows.every(
                                row => row.status === ASN_STATUS.PLANNED && !row.pbsOutStatus,
                            ),
                        }}
                        secondaryActions={[
                            {
                                name: "DOWNLOAD CSV",
                                getFilteredData: true,
                                authOperation: USER_OPERATIONS.GET_CHASSIS_PLAN_ASN,
                                shouldEnable: () => true,
                                actionFn: (filteredData) =>
                                    generateAndDownloadPbsDroppingCsvReport(filteredData),
                            },
                            {
                                name: "Reprint Barcode",
                                actionFn: (selectedRows) => executeReprint(selectedRows),
                                authOperation: RESOURCE_OPERATION_UPDATE,
                                shouldEnable: (selectedRows) => selectedRows.length > 0 && selectedRows.every(
                                    row => row.pbsOutStatus === 'PBS OUT'
                                ),
                            },
                        ]}
                        onRowSelection={handleRowSelection}
                    />
                </div>
            </div>

            <Modal open={openPbsDialog} onClose={handlePbsDialogClose}>
                <div className={styles.popupContainer}>
                    <div className={styles.popupTitle}>
                        <span>
                            <EditIcon className={styles.popupIcon} />
                        </span>
                        {/* <span className={styles.popupTxt}>BIW Validation</span> */}
                    </div>
                    <Box bgcolor="#dadada3d" marginTop="33px" className={styles.popupContent}>
                        {selectedPbsRow && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span className={styles.popupLabel}>VC: {selectedPbsRow.vc}</span>
                                    <span className={styles.popupLabel}>ASN: {selectedPbsRow.asn}</span>
                                </div>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="BIW Serial Number"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={biwSerialNumber}
                                    onChange={(e) => setBiwSerialNumber(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handlePbsSubmit();
                                        }
                                    }}
                                    style={{ backgroundColor: 'white' }}
                                />
                            </>
                        )}
                    </Box>
                    <div className={styles.popupBottomRow}>
                        <button className={styles.popupCancelBt} onClick={handlePbsDialogClose}>
                            Cancel
                        </button>
                        <button onClick={handlePbsSubmit} className={styles.popupSubmitBt}>
                            Submit
                        </button>
                    </div>
                </div>
            </Modal>

            <Dialog open={openSummaryDialog} onClose={handleSummaryClose} maxWidth="md" fullWidth>
                <DialogTitle>PBS Dropping Summary ({moment(selectedDate).format('DD/MM/YYYY')})</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={3}>
                                <KeyboardTimePicker
                                    label="Start Time"
                                    value={summaryStartTime}
                                    onChange={setSummaryStartTime}
                                    ampm={true}
                                    inputVariant="outlined"
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <KeyboardTimePicker
                                    label="End Time"
                                    value={summaryEndTime}
                                    onChange={setSummaryEndTime}
                                    ampm={true}
                                    inputVariant="outlined"
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Button onClick={handleSummarySubmit} color="primary" variant="contained" fullWidth>
                                    Search
                                </Button>
                            </Grid>
                            <Grid item xs={3}>
                                <Button onClick={handleSummaryReset} color="secondary" variant="outlined" fullWidth>
                                    Reset
                                </Button>
                            </Grid>
                        </Grid>

                        {summaryResults.length > 0 ? (
                            <TableContainer component={Paper} style={{ maxHeight: '400px' }}>
                                <MuiTable stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 'bold' }}>Time</TableCell>
                                            <TableCell style={{ fontWeight: 'bold' }}>VC</TableCell>
                                            <TableCell style={{ fontWeight: 'bold' }}>QTY</TableCell>
                                            <TableCell style={{ fontWeight: 'bold' }}>Line</TableCell>
                                            <TableCell style={{ fontWeight: 'bold' }}>QTY</TableCell>
                                            <TableCell style={{ fontWeight: 'bold', backgroundColor: 'yellow' }}>PENDING QTY</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {summaryResults.map((vcData) => (
                                            <React.Fragment key={vcData.vc}>
                                                {vcData.lines.map((lineData, index) => (
                                                    <TableRow key={`${vcData.vc}-${lineData.line}`}>
                                                        {index === 0 && (
                                                            <>
                                                                <TableCell rowSpan={vcData.lines.length}>{summaryTimeRange}</TableCell>
                                                                <TableCell rowSpan={vcData.lines.length}>{vcData.vc}</TableCell>
                                                                <TableCell rowSpan={vcData.lines.length}>{vcData.totalVcQty}</TableCell>
                                                            </>
                                                        )}
                                                        <TableCell>{lineData.line}</TableCell>
                                                        <TableCell>{lineData.lineQty}</TableCell>
                                                        <TableCell style={{ backgroundColor: lineData.linePending > 0 ? 'yellow' : 'inherit' }}>{lineData.linePending}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                        <TableRow style={{ backgroundColor: '#f0f0f0' }}>
                                            <TableCell colSpan={4} align="right" style={{ fontWeight: 'bold' }}>Total</TableCell>
                                            <TableCell style={{ fontWeight: 'bold' }}>{summaryTotals.totalQty}</TableCell>
                                            <TableCell style={{ fontWeight: 'bold', backgroundColor: 'yellow' }}>{summaryTotals.totalPending}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </MuiTable>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="textSecondary" align="center" style={{ padding: '20px' }}>
                                No data found for selected range or perform a search.
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSummaryClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

PBSDroppingUpdate.propTypes = {
    hasActiveEdits: PropTypes.bool.isRequired,
    setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
    PBSDroppingUpdate,
    false,
    RESOURCE_TYPE.CHASSIS
);
